// Rental price for a number of days: the cheapest way to cover them with the
// listing's daily / weekly / monthly rates (the month rate covers up to 30 days).
// Same formula as VTCNODE src/utils/rentalPrice.js — keep them in sync.

export const MIN_BOOKING_DAYS = 1;
export const MAX_BOOKING_DAYS = 31;
const MONTH_DAYS = 30;

export interface Rates {
  daily?: number;
  weekly: number;
  monthly: number;
}
export type PriceBasis = "day" | "week" | "month";

/** Daily rate, or weekly / 7 for listings created before daily prices existed. */
export const dailyRate = (r: Rates) => (r.daily && r.daily > 0 ? r.daily : Math.round((r.weekly / 7) * 100) / 100);

/** Suggested weekly and monthly rates from a daily price (the company can change them). */
export const weeklyFromDaily = (daily: number) => Math.round(daily * 7);
export const monthlyFromWeekly = (weekly: number) => Math.round((weekly * 52) / 12);

export function rentalPrice(days: number, rates: Rates): { total: number; basis: PriceBasis } {
  const daily = dailyRate(rates);
  const weekly = rates.weekly > 0 ? rates.weekly : daily * 7;
  const monthly = rates.monthly > 0 ? rates.monthly : (weekly * 52) / 12;
  const options = ([
    { basis: "day", total: days * daily },
    { basis: "week", total: Math.floor(days / 7) * weekly + (days % 7) * daily },
    { basis: "week", total: Math.ceil(days / 7) * weekly },
    { basis: "month", total: monthly * Math.ceil(days / MONTH_DAYS) },
    { basis: "month", total: monthly * Math.floor(days / MONTH_DAYS) + (days % MONTH_DAYS) * daily },
  ] as { basis: PriceBasis; total: number }[]).filter((o) => o.total > 0);
  const best = options.reduce((a, b) => (b.total < a.total ? b : a), options[0] ?? { basis: "day", total: 0 });
  return { total: Math.round(best.total * 100) / 100, basis: best.basis };
}
